import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import UpdateIcon from "@material-ui/icons/Update";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import { fetchCrawlStatus } from "../../actions/crawls";
import { selectLastCrawlId } from "../../selectors/crawlIdSelector";

const Card = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px 8px 12px;
    border-radius: 10px;
    background: ${({ $tone }) =>
        $tone === "active" ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
      : $tone === "muted"  ? "#f8fafc"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"};
    border: 1px solid ${({ $tone }) =>
        $tone === "active" ? "#6ee7b7"
      : $tone === "muted"  ? "#e2e8f0"
      : "#e2e8f0"};
    box-shadow: ${({ $tone }) =>
        $tone === "active"
            ? "0 1px 2px rgba(16, 185, 129, 0.08), 0 0 0 3px rgba(16, 185, 129, 0.06)"
            : "0 1px 2px rgba(15, 23, 42, 0.04)"};
    transition: box-shadow 200ms, transform 200ms;
    &:hover {
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        transform: translateY(-1px);
    }
    @media (max-width: 600px) {
        gap: 7px;
        padding: 5px 10px 5px 8px;
        border-radius: 8px;
        box-shadow: ${({ $tone }) =>
            $tone === "active"
                ? "0 1px 2px rgba(16, 185, 129, 0.08)"
                : "0 1px 2px rgba(15, 23, 42, 0.04)"};
        &:hover { transform: none; }
    }
`;

const IconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $tone }) => ($tone === "active" ? "#059669" : $tone === "muted" ? "#94a3b8" : "#475569")};
    & svg { font-size: 22px; }
    @media (max-width: 600px) { & svg { font-size: 16px; } }
`;

const Info = styled.div`
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    min-width: 0;
`;

const Primary = styled.div`
    font-size: 0.9rem;
    font-weight: 500;
    color: #94a3b8;
    font-variant-numeric: tabular-nums;
    & .date {
        color: ${({ $tone }) => ($tone === "active" ? "#059669" : "#4338ca")};
        font-weight: 600;
    }
    & .mobile-ago { display: none; color: #64748b; font-weight: 500; }
    @media (max-width: 600px) {
        font-size: 0.78rem;
        & .mobile-ago { display: inline; }
    }
`;

const Meta = styled.div`
    font-size: 0.72rem;
    color: #64748b;
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
    & b { color: #4338ca; font-weight: 600; }
    @media (max-width: 600px) { display: none; }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50%      { opacity: 0.45; transform: scale(0.82); }
`;

const PulseIcon = styled(RadioButtonCheckedIcon)`
    && {
        color: #10b981;
        animation: ${pulse} 1.4s ease-in-out infinite;
    }
`;

const daysAgo = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / 86_400_000);
    if (days <= 0) {
        const hours = Math.floor(ms / 3_600_000);
        if (hours <= 0) return "just now";
        return `${hours}h ago`;
    }
    if (days === 1) return "yesterday";
    return `${days}d ago`;
};

const shortDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" });

export default function LastCrawlBadge() {
    const dispatch = useDispatch();
    const lastCrawlId = useSelector(selectLastCrawlId);
    const crawl = useSelector((s) => s.crawls?.crawls?.[lastCrawlId]);

    useEffect(() => {
        if (lastCrawlId && !crawl) dispatch(fetchCrawlStatus(lastCrawlId));
    }, [dispatch, lastCrawlId, crawl]);

    if (!lastCrawlId) {
        return (
            <Card $tone="muted">
                <IconWrap $tone="muted"><HourglassEmptyIcon /></IconWrap>
                <Info>
                    <Primary>No crawls yet</Primary>
                    <Meta>Waiting for the first run</Meta>
                </Info>
            </Card>
        );
    }
    if (!crawl) return null;

    const inProgress = !crawl.completedAt && (crawl.status?.pendingPages ?? 0) > 0;
    if (inProgress) {
        const visited = Number(crawl.status?.visitedPages || 0).toLocaleString();
        return (
            <Card $tone="active">
                <IconWrap $tone="active"><PulseIcon /></IconWrap>
                <Info>
                    <Primary $tone="active">Crawling now</Primary>
                    <Meta><b>{visited}</b> pages visited</Meta>
                </Info>
            </Card>
        );
    }

    const finished = crawl.completedAt || crawl.status?.lastUpdate;
    if (!finished) return null;

    const visited = Number(crawl.status?.visitedPages || 0).toLocaleString();

    return (
        <Card>
            <IconWrap><UpdateIcon /></IconWrap>
            <Info>
                <Primary>
                    Last crawl · <span className="date">{shortDate(finished)}</span>
                    <span className="mobile-ago"> · {daysAgo(finished)}</span>
                </Primary>
                <Meta>{daysAgo(finished)} · <b>{visited}</b> pages</Meta>
            </Info>
        </Card>
    );
}
